import React, {
  memo, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import {connect, useDispatch} from 'react-redux';
import _ from 'lodash';
import cl from 'classnames';
import Modal from '../../Modal/ModalNew';
import {CartBasketIcon, CloseIcon} from '../../SvgComponents/SvgComponents';
import PERIODS from '../../../constants/periods';
import TariffsDetailsPeriodsItem from './TariffsDetailsPeriodsItem';
import {t} from '../../../services/i18nHandler';
import ReferenceBooksApi from '../../../api/reference-books.api';
import {NotificationError} from '../../../services/notifications';
import {tariffImageByName, tariffInCart} from '../../../constants/tariffs';
import {ActionAddToCart} from '../../../store/actions/actions-cart';

import './ModalTariffDetails.less';
import UtilsHelper from '../../../utils/utils-helper';
import {USER_ROLE} from '../../../constants/user';
import {LanguageContext} from '../../../context/LanguageContext';

function ModalTariffDetails({
  tariffData, onClose, isOpen, canAddToCart, showDemoDetails, cartData, user, options,
}) {
  const language = useContext(LanguageContext);

  const dispatch = useDispatch();

  const [activePeriod, setActivePeriod] = useState(PERIODS.ONE_MONTH.VALUE);

  const [tariff, setTariff] = useState({});

  const [serviceOptions, setServiceOptions] = useState([]);

  const fetchServiceOptions = useCallback(async () => {
    await ReferenceBooksApi.getReferenceBooksByKey('paid-service-options').then((res) => {
      if (res) {
        setServiceOptions(res.data);
      }
    }).catch((error) => {
      NotificationError(error.error_description || error.message);
    }).finally(() => {});
  }, []);

  const onAddToCart = useCallback(() => {
    dispatch(ActionAddToCart(tariff._id, activePeriod, tariff.name, tariff.type));
  }, [tariff, activePeriod]);

  const addedToCart = useMemo(() => (tariffInCart(tariff._id, cartData, user._id)), [tariff, cartData, user]);

  const isDemo = useMemo(() => (tariff.demo), [tariff]);

  const isPromo = useMemo(() => (tariff.promo), [tariff]);

  const canBuy = useMemo(() => (!tariff.default && canAddToCart && !tariff.soon && user.role !== USER_ROLE.ADMINISTRATOR), [canAddToCart, tariff, user]);

  const name = useMemo(() => {
    const tariffName = tariff.name || {};
    return (tariffName[language]);
  }, [tariff, language]);

  const fullDescription = useMemo(() => {
    if (showDemoDetails) {
      return tariff.demoFullDescription || {};
    }
    return tariff.fullDescription || {};
  }, [tariff, showDemoDetails]);

  const additionalDescription = useMemo(() => {
    if (showDemoDetails) {
      return tariff.demoAdditionalDescription || {};
    }
    return tariff.additionalDescription || {};
  }, [tariff, showDemoDetails]);

  const pricePeriods = useMemo(() => (tariff.pricePeriods || []), [tariff]);

  const tariffType = useMemo(() => {
    const {type} = tariff;
    if (isPromo) {
      return t('tariffs.promoTariff');
    }
    if (type === 1) {
      return t('tariffs.accessTariff');
    }
    if (type === 2) {
      return t('tariffs.serviceTariff');
    }
    return null;
  }, [tariff, isPromo, showDemoDetails]);

  const totalHours = useMemo(() => {
    if (isPromo || showDemoDetails) {
      const demoOptions = tariff.demoOptions || {};
      const demoTotalHours = demoOptions.totalHoursRecord || 0;
      return (
        <div className="tariffs_details_services_hours_value">{demoTotalHours}</div>
      );
    }

    const standardOptions = tariff.standardOptions || {};
    const hours = standardOptions.totalHoursRecord || 0;
    const isUnlimited = standardOptions.unlimitedRecord;
    if (isUnlimited) {
      return (
        <div className="tariffs_details_services_hours_infinite" />
      );
    }
    return (<div className="tariffs_details_services_hours_value">{hours}</div>);
  }, [tariff, showDemoDetails]);

  const attributes = useMemo(() => {
    const attrs = tariff.attributes || [];
    return attrs.filter((a) => ((a !== 'UNLIMITED_CREATE_VIDEOPOLL' && a !== 'UNLIMITED_RECORD_VIDEOSESSION')));
  }, [tariff]);

  const attributeDescription = useMemo(() => (attr) => {
    const currentAttr = _.find(serviceOptions, {value: attr}) || {};
    const attrText = currentAttr.text || {};
    return attrText[language] || attr;
  }, [tariff, serviceOptions, language]);

  const accessDays = useMemo(() => {
    if (isPromo || showDemoDetails) {
      const demoOptions = tariff.demoOptions || {};
      const days = demoOptions.daysOfAccess;
      if (days) {
        return (<div className="tariffs_details_services_hours_value">{days}</div>);
      }
    }
    return null;
  }, [tariff, showDemoDetails, isPromo]);

  const tariffImage = useMemo(() => (tariffImageByName(name)), [name]);

  useEffect(() => {
    if (options) {
      setServiceOptions(options);
    } else {
      void fetchServiceOptions();
    }
  }, [options]);

  useEffect(() => {
    if (tariffData) {
      setTariff(tariffData);
    } else {
      setTariff({});
      setActivePeriod(PERIODS.ONE_MONTH.VALUE);
    }
  }, [tariffData]);

  return (
    <Modal onHide={onClose} isOpen={isOpen} className="modal-center">
      <div className="modal-inner">
        <div className="tariffs_details">
          <div className={cl('tariffs_details_head', {
            demo: showDemoDetails,
          })}
          >
            <div className="tariffs_details_head_content">
              {!showDemoDetails && (
                <div className="tariffs_details_head_title">
                  {tariffType}
                </div>
              )}
              <div className="tariffs_details_head_name">
                {showDemoDetails ? t('tariffs.freeDemoAccess') : name}
              </div>
              <div className="tariffs_details_head_text">
                {fullDescription[language]}
              </div>
            </div>
            <div
              className="tariffs_details_head_img"
              style={{
                backgroundImage: `url(${showDemoDetails
                  ? '../../assets/images/tariffs/ic_plan_demo_header.svg'
                  : tariffImage})`,
              }}
            />
            <div role="button" className="tariffs_details_close" onClick={onClose}>
              <CloseIcon />
            </div>
          </div>
          <div className="tariffs_details_content">
            <div className="row">
              <div className="col-lg-6">
                <div className="tariffs_details_text">
                  {additionalDescription[language]}
                </div>
                <div className="tariffs_details_services-list">
                  {attributes.map((a) => (
                    <div className="tariffs_details_services" key={a}>
                      <div className="tariffs_details_services_icon">
                        <i className="icon-check" />
                      </div>
                      <div className="tariffs_details_services_text">
                        {attributeDescription(a)}
                      </div>
                    </div>
                  ))}
                  <div className="tariffs_details_services">
                    <div className="tariffs_details_services_hours">
                      {totalHours}
                      <div className="tariffs_details_services_hours_unit">
                        {t('units.hours')}
                      </div>
                    </div>
                    <div className="tariffs_details_services_text">
                      {t('tariffs.totalHours')}
                    </div>
                  </div>
                  {accessDays && (
                    <div className="tariffs_details_services">
                      <div className="tariffs_details_services_hours">
                        {accessDays}
                        <div className="tariffs_details_services_hours_unit">
                          {t('units.days')}
                        </div>
                      </div>
                      <div className="tariffs_details_services_text">
                        {t('tariffs.accessPeriod')}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="col-lg-6">
                {!!pricePeriods.length && !showDemoDetails && (
                  <div className="tariffs_details_periods">
                    <div className="tariffs_details_periods_label">
                      {canBuy ? t('tariffs.choosePeriod') : t('tariffs.tariffCost')}
                    </div>
                    <div className="tariffs_details_periods_list">
                      {pricePeriods.map((p) => (
                        <TariffsDetailsPeriodsItem
                          key={p._id}
                          period={p.months}
                          amount={p.amount}
                          basePrice={tariff.basePrice}
                          discount={p.discount}
                          prefix={p.prefix}
                          onClick={() => setActivePeriod(p.months)}
                          active={activePeriod === p.months}
                          canAddToCart={canBuy}
                        />
                      ))}
                    </div>
                  </div>
                )}
                {showDemoDetails && (
                  <>
                    <div className="tariffs_details_additional-content">
                      <div className="tariffs_details_additional-content_tariff-image">
                        <img src={tariffImage} alt={name} />
                      </div>
                      <div className="tariffs_details_additional-content_tariff-type">
                        Тариф
                      </div>
                      <div className="tariffs_details_additional-content_tariff-name">
                        {name}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
            {canBuy && (
              <div className="tariffs_details_btn-wrap">
                <button
                  type="button"
                  className={cl('btn', {
                    'btn-base': !addedToCart,
                    'btn-conture': addedToCart,
                  })}
                  onClick={addedToCart ? null : onAddToCart}
                >
                  <CartBasketIcon />
                  {addedToCart ? 'Добавлен в корзину' : 'Добавить в корзину'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}

ModalTariffDetails.defaultProps = {
  language: 'ru',
  tariffData: {},
  user: {},
  canAddToCart: false,
  isOpen: false,
  showDemoDetails: false,
  onClose: () => {},
  cartData: {},
};

ModalTariffDetails.propTypes = {
  tariffData: PropTypes.shape({}),
  user: PropTypes.shape({}),
  cartData: PropTypes.shape({}),
  language: PropTypes.string,
  canAddToCart: PropTypes.bool,
  isOpen: PropTypes.bool,
  showDemoDetails: PropTypes.bool,
  onClose: PropTypes.func,
};

function mapStateToProps({rCart, rUser}) {
  return {
    cartData: rCart.cartData,
    user: rUser,
  };
}

export default connect(mapStateToProps, null)(memo(ModalTariffDetails));
